import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";

const NAVY = "#0B1D3A";
const NAVY_MID = "#1A3A6B";
const GOLD = "#C8A55C";
const GOLD_LIGHT = "#D4B574";
const WHITE = "#FFFFFF";
const OFF_WHITE = "#F8F7F4";
const GRAY_100 = "#E8E6E1";
const GRAY_400 = "#9B9890";
const GRAY_600 = "#6B6860";
const SUCCESS = "#1D9E75";
const CORAL = "#D85A30";

const formatCurrency = (v) => {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v}`;
};

const parseCurrency = (s, min = 0) => {
  const clean = s.replace(/[$,\s]/g, "");
  if (/^\d+(\.\d+)?[mM]$/.test(clean)) return parseFloat(clean) * 1000000;
  if (/^\d+(\.\d+)?[kK]$/.test(clean)) return parseFloat(clean) * 1000;
  const raw = parseFloat(clean);
  // bare decimal like "42.75" interpreted as millions when field is a large-value field
  if (!isNaN(raw) && min >= 500000 && raw < 1000) return raw * 1000000;
  return raw;
};

const parsePercent = (s) => parseFloat(s.replace(/%/g, ""));

function EditableValue({ value, formatFn, parseFn, min, max, onChange }) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState("");

  const handleFocus = () => { setText(formatFn(value).replace(/[$%]/g, "")); setFocused(true); };
  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFn(text, min);
    if (!isNaN(parsed)) onChange(Math.min(max, Math.max(min, Math.round(parsed))));
  };
  const handleKeyDown = (e) => { if (e.key === "Enter") e.target.blur(); };

  return (
    <input
      type="text"
      value={focused ? text : formatFn(value)}
      onChange={(e) => setText(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        width: 100,
        textAlign: "right",
        border: `1px solid ${focused ? GOLD : GRAY_100}`,
        borderRadius: 6,
        padding: "4px 8px",
        background: WHITE,
        fontSize: 16,
        fontWeight: 500,
        color: NAVY,
        fontFamily: "'DM Sans', sans-serif",
        outline: "none",
        flexShrink: 0,
        transition: "border-color 0.2s",
      }}
    />
  );
}

function StackVisualiser({ onComplete }) {
  const [gdv, setGdv] = useState(10000000);
  const [totalCosts, setTotalCosts] = useState(7500000);
  const [seniorLvr, setSeniorLvr] = useState(65);
  const [mezzEnabled, setMezzEnabled] = useState(true);
  const [mezzLvr, setMezzLvr] = useState(80);

  const seniorDebt = Math.round(gdv * (seniorLvr / 100));
  const maxMezz = Math.round(gdv * (mezzLvr / 100)) - seniorDebt;
  const mezzDebt = mezzEnabled ? Math.max(0, maxMezz) : 0;
  const totalDebt = seniorDebt + mezzDebt;
  const equity = Math.max(0, totalCosts - totalDebt);
  const equityPercent = totalCosts > 0 ? Math.round((equity / totalCosts) * 100) : 0;
  const overallLvr = gdv > 0 ? Math.round((totalDebt / gdv) * 100) : 0;
  const profitOnCost = totalCosts > 0 ? Math.round(((gdv - totalCosts) / totalCosts) * 100) : 0;

  const layers = [
    { label: "Equity", value: equity, color: CORAL, textColor: WHITE, percent: equityPercent },
    ...(mezzEnabled && mezzDebt > 0
      ? [{ label: "Mezzanine", value: mezzDebt, color: GOLD, textColor: NAVY, percent: totalCosts > 0 ? Math.round((mezzDebt / totalCosts) * 100) : 0 }]
      : []),
    { label: "Senior debt", value: seniorDebt, color: NAVY_MID, textColor: WHITE, percent: totalCosts > 0 ? Math.round((seniorDebt / totalCosts) * 100) : 0 },
  ];

  const sliderLabel = {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    color: GRAY_400,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 6,
  };

  return (
    <div className="calc-grid" style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "3rem", alignItems: "start" }}>
      <div>
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={sliderLabel}>Gross Development Value (GDV)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="range" min={1000000} max={100000000} step={500000} value={gdv}
              onChange={(e) => setGdv(Number(e.target.value))} style={{ flex: 1, accentColor: NAVY }} />
            <EditableValue value={gdv} formatFn={formatCurrency} parseFn={parseCurrency} min={500000} max={100000000} onChange={setGdv} />
          </div>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label style={sliderLabel}>Total Development Costs</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="range" min={500000} max={80000000} step={250000} value={totalCosts}
              onChange={(e) => setTotalCosts(Number(e.target.value))} style={{ flex: 1, accentColor: NAVY }} />
            <EditableValue value={totalCosts} formatFn={formatCurrency} parseFn={parseCurrency} min={250000} max={80000000} onChange={setTotalCosts} />
          </div>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label style={sliderLabel}>Senior Debt LVR</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="range" min={40} max={75} step={1} value={seniorLvr}
              onChange={(e) => setSeniorLvr(Number(e.target.value))} style={{ flex: 1, accentColor: NAVY }} />
            <EditableValue value={seniorLvr} formatFn={(v) => `${v}%`} parseFn={parsePercent} min={40} max={75} onChange={setSeniorLvr} />
          </div>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <label style={{ ...sliderLabel, marginBottom: 0 }}>Mezzanine Finance</label>
            <button
              onClick={() => setMezzEnabled(!mezzEnabled)}
              style={{ width: 44, height: 24, borderRadius: 12, border: "none", background: mezzEnabled ? SUCCESS : GRAY_100, cursor: "pointer", position: "relative", transition: "background 0.2s" }}
            >
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: WHITE, position: "absolute", top: 3, left: mezzEnabled ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
            </button>
          </div>
          {mezzEnabled && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="range" min={seniorLvr + 1} max={90} step={1} value={mezzLvr}
                onChange={(e) => setMezzLvr(Number(e.target.value))} style={{ flex: 1, accentColor: GOLD }} />
              <EditableValue value={mezzLvr} formatFn={(v) => `${v}%`} parseFn={parsePercent} min={seniorLvr + 1} max={90} onChange={setMezzLvr} />
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ padding: "6px 14px", background: WHITE, border: `1px solid ${GRAY_100}`, borderRadius: 20, fontSize: 12 }}>
            <span style={{ color: GRAY_400, marginRight: 4 }}>Profit on cost</span>
            <strong style={{ color: profitOnCost >= 15 ? SUCCESS : CORAL }}>{profitOnCost}%</strong>
          </div>
          <div style={{ padding: "6px 14px", background: WHITE, border: `1px solid ${GRAY_100}`, borderRadius: 20, fontSize: 12 }}>
            <span style={{ color: GRAY_400, marginRight: 4 }}>LVR (GDV)</span>
            <strong style={{ color: NAVY }}>{overallLvr}%</strong>
          </div>
        </div>
      </div>

      <div>
        <div style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${GRAY_100}`, height: 300 }}>
          {layers.map((layer, i) => {
            const h = Math.max(layer.percent * 2.8, 36);
            return (
              <div key={layer.label} style={{ background: layer.color, height: h, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderBottom: i < layers.length - 1 ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
                <span style={{ color: layer.textColor, fontSize: 13, fontWeight: 500 }}>{layer.label}</span>
                <span style={{ color: layer.textColor, fontSize: 13, fontWeight: 500 }}>{formatCurrency(layer.value)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
          <div style={{ background: WHITE, border: `1px solid ${GRAY_100}`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: GRAY_400, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Total debt</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: NAVY }}>{formatCurrency(totalDebt)}</div>
          </div>
          <div style={{ background: WHITE, border: `1px solid ${GRAY_100}`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: GRAY_400, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Equity required</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: CORAL }}>{formatCurrency(equity)}</div>
          </div>
        </div>
        <button
          onClick={onComplete}
          style={{ width: "100%", marginTop: 12, padding: "14px 24px", background: GOLD, color: NAVY, border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", letterSpacing: "0.02em", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}
          onMouseEnter={(e) => { e.target.style.background = GOLD_LIGHT; e.target.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.target.style.background = GOLD; e.target.style.transform = "translateY(0)"; }}
        >
          Get my structure reviewed →
        </button>
      </div>
    </div>
  );
}

function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    gdv: "",
    da_status: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError("Please enter your full name."); return; }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) { setError("Please enter a valid email address."); return; }
    setError("");
    setLoading(true);
    try {
      const [emailResult, sharepointResult] = await Promise.allSettled([
        emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          {
            from_name: form.name,
            from_email: form.email,
            phone: form.phone,
            project_type: form.projectType,
            gdv: form.gdv,
            da_status: form.da_status,
            message: form.message,
          },
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        ),
        fetch("/api/submit-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone: form.phone,
            projectType: form.projectType,
            gdv: form.gdv,
            da_status: form.da_status,
            message: form.message,
          }),
        }).then((r) => { if (!r.ok) throw new Error("SharePoint write failed"); }),
      ]);

      if (sharepointResult.status === "rejected") {
        console.error("SharePoint error:", sharepointResult.reason);
      }

      if (emailResult.status === "rejected" && sharepointResult.status === "rejected") {
        setError("Something went wrong. Please try again or email us directly.");
        return;
      }

      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead");
      }
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    border: `1px solid ${GRAY_100}`,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    background: WHITE,
    color: NAVY,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    color: GRAY_400,
    marginBottom: 6,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  };

  const chevronBg = `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%239B9890' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

  const selectStyle = {
    ...inputStyle,
    appearance: "none",
    backgroundImage: chevronBg,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 14px center",
    cursor: "pointer",
  };

  const focusGold = (e) => { e.target.style.borderColor = GOLD; };
  const blurGray = (e) => { e.target.style.borderColor = GRAY_100; };

  if (submitted) {
    return (
      <div style={{ padding: "3rem 0", textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#E1F5EE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={SUCCESS} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 style={{ fontSize: 22, fontWeight: 400, color: NAVY, marginBottom: 8, fontFamily: "'DM Serif Display', serif" }}>
          We've received your details
        </h3>
        <p style={{ fontSize: 15, color: GRAY_600, lineHeight: 1.6, maxWidth: 400, margin: "0 auto" }}>
          A member of the Mersey Capital team will be in touch within 4 business hours to discuss your funding structure.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: OFF_WHITE, border: `0.5px solid ${GRAY_100}`, borderRadius: 12, padding: 16, marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: NAVY, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: GOLD, fontFamily: "'DM Sans', sans-serif" }}>LR</span>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: NAVY }}>Louis Ramsey</div>
          <div style={{ fontSize: 13, color: GRAY_600 }}>Development Finance Specialist</div>
          <div style={{ fontSize: 13, color: GOLD }}>0431 971 239 — responds within 4 business hours</div>
        </div>
      </div>

      <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Full name</label>
          <input style={inputStyle} value={form.name} onChange={update("name")} placeholder="Your name" onFocus={focusGold} onBlur={blurGray} />
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input style={inputStyle} value={form.phone} onChange={update("phone")} placeholder="0400 000 000" onFocus={focusGold} onBlur={blurGray} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Email</label>
        <input style={inputStyle} value={form.email} onChange={update("email")} placeholder="you@company.com" type="email" onFocus={focusGold} onBlur={blurGray} />
      </div>

      <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Project type</label>
          <select style={selectStyle} value={form.projectType} onChange={update("projectType")} onFocus={focusGold} onBlur={blurGray}>
            <option value="">Select type</option>
            <option value="duplex">Duplex / dual occupancy</option>
            <option value="townhouse">Townhouses</option>
            <option value="apartment">Apartments</option>
            <option value="seniors">Seniors living</option>
            <option value="industrial">Industrial / commercial</option>
            <option value="land">Land subdivision</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>DA status</label>
          <select style={selectStyle} value={form.da_status} onChange={update("da_status")} onFocus={focusGold} onBlur={blurGray}>
            <option value="">Select status</option>
            <option value="approved">DA Approved</option>
            <option value="lodged">DA Lodged</option>
            <option value="pre-da">Pre-DA</option>
            <option value="not-sure">Not Sure</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Estimated GDV</label>
        <select style={selectStyle} value={form.gdv} onChange={update("gdv")} onFocus={focusGold} onBlur={blurGray}>
          <option value="">Select range</option>
          <option value="u2m">Under $2M</option>
          <option value="2-5m">$2M – $5M</option>
          <option value="5-10m">$5M – $10M</option>
          <option value="10-25m">$10M – $25M</option>
          <option value="25-50m">$25M – $50M</option>
          <option value="50m+">$50M+</option>
        </select>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Tell us about your project (optional)</label>
        <textarea
          style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
          value={form.message}
          onChange={update("message")}
          placeholder="Site location, number of dwellings, any other details..."
          onFocus={focusGold}
          onBlur={blurGray}
        />
      </div>

      {error && <p style={{ fontSize: 13, color: CORAL, marginBottom: 12, textAlign: "center" }}>{error}</p>}

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{ width: "100%", padding: "16px", background: loading ? GRAY_100 : NAVY, color: loading ? GRAY_400 : WHITE, border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.02em", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}
        onMouseEnter={(e) => { if (!loading) e.target.style.background = NAVY_MID; }}
        onMouseLeave={(e) => { if (!loading) e.target.style.background = NAVY; }}
      >
        {loading ? "Sending…" : "Submit enquiry"}
      </button>
      <p style={{ fontSize: 12, color: GRAY_400, textAlign: "center", marginTop: 10 }}>
        Strictly confidential. We respond within 4 business hours.
      </p>
    </div>
  );
}

export default function StackedLanding() {
  const [scrolled, setScrolled] = useState(false);
  const calcRef = useRef(null);
  const howItWorksRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToCalc = () => calcRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToHowItWorks = () => howItWorksRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: WHITE, minHeight: "100vh", color: NAVY }}>
      <style>{`
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-right { display: none !important; }
          .how-grid { grid-template-columns: 1fr 1fr !important; }
          .calc-grid { grid-template-columns: 1fr !important; }
          .trust-bar { flex-wrap: wrap !important; }
          .trust-item { width: calc(50% - 8px) !important; }
          .form-grid { grid-template-columns: 1fr !important; }
          .fund-grid { grid-template-columns: 1fr !important; }
          .nav-phone { display: none !important; }
          .hero-h1 { font-size: 34px !important; }
          .explainer-inner { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .how-grid { grid-template-columns: 1fr !important; }
          .trust-item { width: 100% !important; }
        }
      `}</style>

      {/* 1. NAV */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: WHITE, borderBottom: `1px solid ${GRAY_100}`, boxShadow: scrolled ? "0 2px 12px rgba(11,29,58,0.07)" : "none", transition: "box-shadow 0.2s" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="10" width="12" height="3" rx="0.5" fill={GOLD} opacity="0.9" />
                <rect x="3" y="6" width="10" height="3" rx="0.5" fill={GOLD} opacity="0.7" />
                <rect x="4" y="2" width="8" height="3" rx="0.5" fill={GOLD} opacity="0.5" />
              </svg>
            </div>
            <span style={{ fontSize: 20, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, letterSpacing: "-0.01em" }}>Stacked</span>
            <span style={{ fontSize: 12, color: GRAY_400 }}>by Mersey Capital</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <span className="nav-phone" style={{ fontSize: 14, color: GRAY_600 }}>0431 971 239</span>
            <button
              onClick={scrollToCalc}
              style={{ padding: "10px 20px", background: NAVY, color: WHITE, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer", transition: "background 0.2s", fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => { e.target.style.background = NAVY_MID; }}
              onMouseLeave={(e) => { e.target.style.background = NAVY; }}
            >
              Get funding
            </button>
          </div>
        </div>
      </nav>

      {/* 2. HERO */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "5rem 2rem 4rem" }}>
        <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "60% 40%", gap: "4rem", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>
              Development Finance — Structured
            </div>
            <h1 className="hero-h1" style={{ fontSize: 48, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, lineHeight: 1.1, marginBottom: 20, letterSpacing: "-0.02em" }}>
              Know your capital stack before you call a lender
            </h1>
            <p style={{ fontSize: 15, color: GRAY_600, lineHeight: 1.6, marginBottom: 32, maxWidth: 460 }}>
              Model your senior debt, mezzanine, and equity in 60 seconds. Then let Mersey Capital match your structure against 40+ lenders.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={scrollToCalc}
                style={{ padding: "13px 28px", background: GOLD, color: NAVY, border: "none", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}
                onMouseEnter={(e) => { e.target.style.background = GOLD_LIGHT; e.target.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.target.style.background = GOLD; e.target.style.transform = "translateY(0)"; }}
              >
                Build my stack →
              </button>
              <button
                onClick={scrollToHowItWorks}
                style={{ padding: "13px 28px", background: "transparent", color: NAVY, border: `1px solid ${GRAY_100}`, borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: "pointer", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}
                onMouseEnter={(e) => { e.target.style.borderColor = NAVY; }}
                onMouseLeave={(e) => { e.target.style.borderColor = GRAY_100; }}
              >
                How it works
              </button>
            </div>
          </div>
          <div className="hero-right">
            <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${GRAY_100}`, boxShadow: "0 8px 32px rgba(11,29,58,0.08)" }}>
              <div style={{ background: CORAL, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: WHITE, fontSize: 14, fontWeight: 500 }}>Equity</span>
                <span style={{ color: WHITE, fontSize: 14, fontWeight: 500 }}>$1.5M — 20%</span>
              </div>
              <div style={{ background: GOLD, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: NAVY, fontSize: 14, fontWeight: 500 }}>Mezzanine</span>
                <span style={{ color: NAVY, fontSize: 14, fontWeight: 500 }}>$1.1M — 15%</span>
              </div>
              <div style={{ background: NAVY_MID, padding: "22px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: WHITE, fontSize: 14, fontWeight: 500 }}>Senior debt</span>
                <span style={{ color: WHITE, fontSize: 14, fontWeight: 500 }}>$4.9M — 65%</span>
              </div>
              <div style={{ background: WHITE, padding: "16px 22px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, borderTop: `1px solid ${GRAY_100}` }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: GRAY_400, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>GDV</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: NAVY }}>$10.0M</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: GRAY_400, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Equity required</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: CORAL }}>$1.5M</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HOW IT WORKS */}
      <section ref={howItWorksRef} style={{ background: NAVY, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12, textAlign: "center" }}>
            The Process
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: WHITE, textAlign: "center", marginBottom: "3rem" }}>
            Four steps to your funding structure
          </h2>
          <div className="how-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 32 }}>
            {[
              { num: "01", title: "Model your stack", desc: "Adjust the sliders to see how senior debt, mezzanine, and equity interact in your deal." },
              { num: "02", title: "Submit your project", desc: "Tell us about your development — type, GDV, DA status. Takes under two minutes." },
              { num: "03", title: "We structure the deal", desc: "We match against 40+ lenders and come back with a tailored recommendation within 72 hours." },
              { num: "04", title: "Settle with confidence", desc: "From indicative terms to settlement, Mersey Capital manages the process end to end." },
            ].map((step, i) => (
              <div key={i}>
                <div style={{ fontSize: 40, fontWeight: 500, color: "rgba(200,165,92,0.15)", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>{step.num}</div>
                <h3 style={{ fontSize: 16, fontWeight: 500, color: WHITE, marginBottom: 8 }}>{step.title}</h3>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. CALCULATOR */}
      <section ref={calcRef} style={{ background: OFF_WHITE, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
            Step 1 — Model Your Stack
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, marginBottom: "2.5rem" }}>
            See exactly how your deal stacks up
          </h2>
          <StackVisualiser onComplete={scrollToForm} />
        </div>
      </section>

      {/* 5. TRUST BAR */}
      <section style={{ background: WHITE, borderTop: `1px solid ${GRAY_100}`, borderBottom: `1px solid ${GRAY_100}`, padding: "1.25rem 2rem" }}>
        <div className="trust-bar" style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
          {[
            "AFCA Member 122211",
            "Connective Credit Rep",
            "40+ Lender Relationships",
            "$250M+ Settled",
            "72hr Indicative Terms",
          ].map((item, i) => (
            <div key={i} className="trust-item" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: GRAY_600, fontWeight: 500, whiteSpace: "nowrap" }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 6. LEAD FORM */}
      <section ref={formRef} style={{ background: WHITE, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
            Step 2 — Get Your Structure
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, marginBottom: 8 }}>
            Let's structure your funding
          </h2>
          <p style={{ fontSize: 15, color: GRAY_600, lineHeight: 1.6, marginBottom: "2rem" }}>
            Tell us about your project. We'll come back with a tailored capital stack recommendation within 72 hours.
          </p>
          <LeadForm />
        </div>
      </section>

      {/* 7. CAPITAL STACK EXPLAINER */}
      <section style={{ background: OFF_WHITE, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, textAlign: "center", marginBottom: 8 }}>
            Understanding the capital stack
          </h2>
          <p style={{ fontSize: 15, color: GRAY_600, textAlign: "center", marginBottom: "2.5rem", maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            Every development project has a layered funding structure. Position in the stack determines repayment priority, risk, and cost of capital.
          </p>
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            {[
              { label: "Common equity", range: "Top of stack", color: CORAL, textWhite: true, desc: "Developer's own capital. Last to be repaid, first to absorb losses. Typically 10–30% of total costs." },
              { label: "Preferred equity", range: "Below equity", color: "#E88A5A", textWhite: false, desc: "Investor capital with a fixed return priority above common equity. No registered security, but sits above the developer's equity." },
              { label: "Mezzanine debt", range: "Mid stack", color: GOLD, textWhite: false, desc: "Second-charge debt bridging the gap between senior debt and equity. Typically takes total leverage to 75–85% of costs." },
              { label: "Senior debt", range: "Base of stack", color: NAVY_MID, textWhite: true, desc: "First-charge, first-ranking security. Bank or non-bank lender. Typically 55–70% LTGDV or up to 90% of costs." },
            ].map((layer, i, arr) => (
              <div key={i} style={{ background: layer.color, padding: "20px 24px", borderBottom: i < arr.length - 1 ? "2px solid rgba(0,0,0,0.1)" : "none", borderRadius: i === 0 ? "12px 12px 0 0" : i === arr.length - 1 ? "0 0 12px 12px" : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 16, fontWeight: 500, color: layer.textWhite ? WHITE : NAVY }}>{layer.label}</span>
                  <span style={{ fontSize: 11, color: layer.textWhite ? "rgba(255,255,255,0.6)" : "rgba(11,29,58,0.45)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{layer.range}</span>
                </div>
                <p style={{ fontSize: 13, color: layer.textWhite ? "rgba(255,255,255,0.7)" : "rgba(11,29,58,0.6)", margin: 0, lineHeight: 1.5 }}>{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHAT WE FUND */}
      <section style={{ background: WHITE, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: NAVY, textAlign: "center", marginBottom: "2.5rem" }}>
            What we fund
          </h2>
          <div className="fund-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {[
              { title: "Residential development", desc: "Duplexes, townhouses, apartments, and house-and-land. From DA-approved sites through to completed stock." },
              { title: "Seniors living", desc: "Purpose-built seniors housing and aged care developments, including specialist funding structures." },
              { title: "Industrial and commercial", desc: "Warehouses, factory units, mixed-use, and commercial builds. Flexible structures for non-residential assets." },
              { title: "Land subdivision", desc: "Civil works and land banking finance. Structured to release equity as lots settle." },
              { title: "Residual stock", desc: "Post-completion refinance for unsold stock. Free up working capital while you wait for the right buyer." },
              { title: "Construction bridging", desc: "Short-term capital to bridge timing gaps between settlements, refinances, and construction drawdowns." },
            ].map((item, i) => (
              <div key={i} style={{ padding: 20, background: WHITE, border: `0.5px solid ${GRAY_100}`, borderLeft: `3px solid ${GOLD}`, borderRadius: "0 12px 12px 0" }}>
                <h3 style={{ fontSize: 15, fontWeight: 500, color: NAVY, marginBottom: 8 }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: GRAY_600, margin: 0, lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section style={{ background: NAVY, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 32, fontWeight: 400, fontFamily: "'DM Serif Display', serif", color: WHITE, marginBottom: 12 }}>
            Ready to structure your next project?
          </h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginBottom: "2rem" }}>
            Use the calculator to model your funding, then submit your details. We'll come back with a tailored structure within 72 hours.
          </p>
          <button
            onClick={scrollToForm}
            style={{ padding: "16px 40px", background: GOLD, color: NAVY, border: "none", borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif" }}
            onMouseEnter={(e) => { e.target.style.background = GOLD_LIGHT; e.target.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.target.style.background = GOLD; e.target.style.transform = "translateY(0)"; }}
          >
            Get my structure reviewed →
          </button>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer style={{ background: NAVY, borderTop: "1px solid rgba(255,255,255,0.08)", padding: "2.5rem 2rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 24 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 5, background: "rgba(200,165,92,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="10" width="12" height="3" rx="0.5" fill={GOLD} opacity="0.9" />
                    <rect x="3" y="6" width="10" height="3" rx="0.5" fill={GOLD} opacity="0.7" />
                    <rect x="4" y="2" width="8" height="3" rx="0.5" fill={GOLD} opacity="0.5" />
                  </svg>
                </div>
                <span style={{ fontSize: 18, fontWeight: 400, color: WHITE, fontFamily: "'DM Serif Display', serif" }}>Stacked</span>
                <span style={{ fontSize: 12, color: GRAY_400 }}>by Mersey Capital</span>
              </div>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", maxWidth: 380, lineHeight: 1.6, margin: 0 }}>
                Mersey Capital is a division of Mersey Group Holdings Pty Ltd. AFCA Member 122211.
                Authorised Credit Representative under Connective ACL. Information provided is general
                in nature and does not constitute financial advice.
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Get in touch</div>
              <a href="mailto:info@merseygroup.com.au" style={{ fontSize: 14, color: GOLD, textDecoration: "none" }}>info@merseygroup.com.au</a>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "2rem", fontSize: 11, color: "rgba(255,255,255,0.2)" }}>
            &copy; {new Date().getFullYear()} Mersey Group Holdings Pty Ltd. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
